/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Typography, Divider, Button, Popover } from "@mui/material";
import qs from "query-string";
import React, { useState, useEffect, useRef, useCallback } from "react";
import LoadingSpinner from "../../components/Composables/CircularProgressBar";
import CountInfo from "../../components/Composables/TilesCountInfo";
import API from "../../Utils/Interceptor/ApiMiddleware";
import DynamicDataGrid from "../../components/Composables/DynamicDataGrid";
import AddIcon from "@mui/icons-material/Add";
import AddCompany from "../../components/Composables/AddCompany";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import formatters from "../../components/Composables/DataGridFormatters";
import { debounce } from "lodash";
import DynamicForm from "../../components/Composables/DynamicForm";
import { useNavigate, useLocation } from "react-router-dom";

function Companies() {
  const [loading, setLoading] = useState({
    count: false,
    grid: false,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [formState, setFormState] = useState({
    companyName: null,
    status: null,
    createdBy: null,
    state: null,
    city: null,
  });
  const [appliedFilters, setAppliedFilters] = useState({
    companyName: null,
    status: null,
    createdBy: null,
    state: null,
    city: null,
  });

  const debounceTimeout = useRef(null);
  const latestInputValue = useRef("");
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tempFormState, setTempFormState] = useState(formState);
  const [companiesData, setCompaniesData] = useState({
    countData: [],
    listData: [],
    paginationModel: {
      pageSize: 25,
      page: 0,
    },
  });

  const [formConfigurations, setFormConfigurations] = useState([
    {
      label: "Company Name",
      type: "autocomplete-multiple",
      name: "companyName",
      options: [],
      text: "Company Name",
    },
    {
      label: "Created By",
      type: "autocomplete-multiple",
      name: "createdBy",
      options: [],
      text: "Created By",
    },
    {
      label: "Status",
      type: "autocomplete-multiple",
      name: "status",
      options: [
        { value: "active", label: "Active" },
        { value: "closed", label: "Closed" },
      ],
      text: "Status",
    },
    {
      label: "State",
      type: "autocomplete-multiple",
      name: "state",
      options: [],
      text: "State",
    },
    {
      label: "City",
      type: "autocomplete-multiple",
      name: "city",
      options: [],
      text: "City",
    },
  ]);

  const fetchCompaniesGridData = useCallback(async () => {
    const payload = {
      filterCompanyName: appliedFilters.companyName,
      filterCreatedBy: appliedFilters.createdBy,
      filterStatus: appliedFilters.status,
      filterState: appliedFilters.state,
      filterCity: appliedFilters.city,
      page: companiesData.paginationModel.page + 1,
      pageSize: companiesData.paginationModel.pageSize,
    };
    setLoading((prev) => ({
      ...prev,
      grid: true,
    }));
    try {
      const response = await API.post("/company/get-company-details", payload);
      const apiData = response.data.data;
      setCompaniesData((prevState) => ({
        ...prevState,
        listData: apiData,
      }));
    } catch (error) {
      console.error("Error fetching Companies grid data:", error);
    } finally {
      setLoading((prev) => ({
        ...prev,
        grid: false,
      }));
    }
  }, [
    appliedFilters.companyName,
    appliedFilters.createdBy,
    appliedFilters.status,
    appliedFilters.state,
    appliedFilters.city,
    companiesData.paginationModel.page,
    companiesData.paginationModel.pageSize,
  ]);
  const fetchCompaniesData = async () => {
    setLoading((prev) => ({
      ...prev,
      count: true,
    }));
    try {
      const payload = {
        tile_type: "company_count",
      };
      const response = await API.post("tiles/get-count", payload);
      const apiData = response.data.data;

      setCompaniesData((prevState) => ({
        ...prevState,
        countData: apiData,
      }));
    } catch (error) {
      console.error("Error fetching companies data:", error);
    } finally {
      setLoading((prev) => ({
        ...prev,
        count: false,
      }));
    }
  };

  const fetchCompanyFilterData = async (filterParams) => {
    try {
      const response = await API.get("/company/get-company-filter-details", {
        params: filterParams,
      });

      if (response) {
        const apiData = response.data.data;

        setFormConfigurations((prevConfig) =>
          prevConfig.map((config) => {
            if (apiData.length > 0 && apiData[0].hasOwnProperty(config.name)) {
              const newOptions = apiData
                .map((item) => item[config.name])
                .filter((val) => val && val.trim() !== "")
                .map((val) => ({
                  value: val,
                  label: val,
                }));

              const mergedOptions = [
                ...newOptions,
                ...(config.options || []),
              ].reduce((acc, option) => {
                if (!acc.find((o) => o.value === option.value)) {
                  acc.push(option);
                }
                return acc;
              }, []);

              return { ...config, options: mergedOptions };
            }
            return config;
          })
        );
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  };

  const debouncedFetchCompanyFilterData = debounce(fetchCompanyFilterData, 500);

  useEffect(() => {
    fetchCompaniesData();
  }, []);

  useEffect(() => {
    const queryParams = qs.parse(location.search);

    const {
      page = 1,
      companyName,
      status,
      createdBy,
      state,
      city,
    } = queryParams;

    const filtersFromQuery = {
      companyName: companyName
        ? Array.isArray(companyName)
          ? companyName
          : [companyName]
        : null,
      status: status ? (Array.isArray(status) ? status : [status]) : null,
      createdBy: createdBy
        ? Array.isArray(createdBy)
          ? createdBy
          : [createdBy]
        : null,
      state: state ? (Array.isArray(state) ? state : [state]) : null,
      city: city ? (Array.isArray(city) ? city : [city]) : null,
    };

    setFormState(filtersFromQuery);
    setAppliedFilters(filtersFromQuery);

    setCompaniesData((prev) => ({
      ...prev,
      paginationModel: {
        ...prev.paginationModel,
        page: parseInt(page) - 1,
      },
    }));
  }, [location.search]);

  useEffect(() => {
    fetchCompaniesGridData();
  }, [fetchCompaniesGridData]);

  useEffect(() => {
    if (anchorEl) {
      const fieldsToFetch = formConfigurations
        .filter(
          (field) =>
            field.type === "autocomplete-multiple" &&
            !(
              field.options &&
              field.options.length > 0 &&
              field.options.every((opt) => opt.value && opt.label)
            )
        )
        .map((field) => field.name);

      const payload = {};

      fieldsToFetch.forEach((field) => {
        if (formState[field] !== null && formState[field] !== undefined) {
          payload[`${field}Search`] = "";
        }
      });

      if (Object.keys(payload).length > 0) {
        fetchCompanyFilterData(payload);
      }
    }
  }, [anchorEl, formConfigurations]);
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setTempFormState(formState);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleResetFilter = async () => {
    const resetFilters = {
      companyName: null,
      status: null,
      createdBy: null,
      state: null,
      city: null,
    };

    setFormState(resetFilters);
    setCompaniesData((prev) => ({
      ...prev,
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    }));

    const query = qs.stringify(
      {
        ...resetFilters,
        page: 1,
      },
      { skipNull: true, skipEmptyString: true }
    );
    navigate(`/companies?${query}`);
    handleClose();
  };

  const handleApplyFilter = () => {
    const query = qs.stringify(
      {
        ...tempFormState,
        page: 1,
      },
      { skipNull: true, skipEmptyString: true }
    );

    setFormState(tempFormState);
    setAppliedFilters(tempFormState);
    navigate(`/companies?${query}`);
    handleClose();
  };

  const handleChange = (key, value, inputValue = null) => {
    setTempFormState((prev) => ({
      ...prev,
      [key]: value,
      [`${key}Input`]: inputValue !== null ? inputValue : prev[`${key}Input`],
    }));

    if (inputValue !== null) {
      latestInputValue.current = inputValue;

      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => {
        fetchCompanyFilterData({ [`${key}Search`]: latestInputValue.current });
      }, 1000);
    }
  };

  const handleRowSelection = (row) => {
    navigate(`/companies-description/${row.id}`, {
      state: {
        id: row.id,
      },
    });
  };
  const handleAddCompany = () => {
    setOpen(true);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "16px",
        backgroundColor: "#E3F2FD",
        height: "100%",
        boxSizing: "border-box",
        borderRadius: "24px 0 0 0",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "16px",
          marginBottom: "16px",
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: "600" }}>
          Companies
        </Typography>

        {loading.count ? (
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "12px",
              flex: 1,
              textAlign: "center",
              padding: "16px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "100px",
            }}
          >
            <LoadingSpinner />
          </Box>
        ) : companiesData.countData.length > 0 ? (
          <CountInfo sections={companiesData.countData} showHeader={false} />
        ) : (
          <CountInfo sections={[]} showHeader={false} />
        )}
      </Box>
      <Box
        sx={{
          borderRadius: "12px",
          padding: "16px",
          backgroundColor: "white",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
            List of Companies
          </Typography>

          <Box sx={{ display: "flex", gap: "12px" }}>
            <Button
              sx={{
                backgroundColor: "#2196F3",
                padding: "6px 10px",
                color: "white",
                display: "flex",
                alignItems: "center",
                borderRadius: "5px",
              }}
              onClick={handleAddCompany}
            >
              <AddIcon sx={{ fontSize: 20, marginRight: "8px" }} />
              <Typography sx={{ fontSize: "14px" }}>ADD COMPANY</Typography>
            </Button>

            <Button
              sx={{
                backgroundColor: "#2196F3",
                padding: "6px 10px",
                color: "white",
                display: "flex",
                alignItems: "center",
                borderRadius: "5px",
              }}
              onClick={(e) => handleOpen(e)}
            >
              <FilterAltOutlinedIcon
                sx={{ fontSize: 20, marginRight: "8px" }}
              />
              <Typography sx={{ fontSize: "14px" }}>FILTER</Typography>
            </Button>
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <DynamicDataGrid
            rows={companiesData?.listData.companyDetails || []}
            totalRows={companiesData?.listData?.totalCompanies || 0}
            showCheckbox={false}
            onRowSelection={handleRowSelection}
            formatters={formatters}
            paginationModel={companiesData.paginationModel}
            onPaginationModelChange={(newPaginationModel) => {
              setCompaniesData((prevState) => ({
                ...prevState,
                paginationModel: newPaginationModel,
              }));

              navigate(
                `/companies?${qs.stringify(
                  {
                    ...formState,
                    page: newPaginationModel.page + 1,
                  },
                  { skipNull: true, skipEmptyString: true }
                )}`
              );
            }}
            loading={loading.grid}
          />
        </Box>
        {open && (
          <AddCompany
            open={open}
            handleClose={() => setOpen(false)}
            fetchCompaniesGridData={fetchCompaniesGridData}
          />
        )}
      </Box>
      <Popover
        anchorReference="anchorE1"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ padding: 2, width: 300 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Company Filter
          </Typography>
          <Divider sx={{ my: 1 }} />
          {formConfigurations ? (
            <DynamicForm
              formData={formConfigurations}
              debouncedFilterData={debouncedFetchCompanyFilterData}
              filterData={fetchCompanyFilterData}
              handleChange={handleChange}
              formState={tempFormState}
            />
          ) : (
            <Typography variant="body1">No filters available</Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button onClick={handleResetFilter}>Reset</Button>
            <Button variant="contained" onClick={handleApplyFilter}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
export default Companies;
